import { AnimatePresence, motion, type Variants } from 'framer-motion';
import { ArrowLeft, ArrowRight, Bell, FileText, X } from 'lucide-react';
import { useTheme } from 'next-themes';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { AIProvider } from '../../blocksuite/presets/ai/provider';
import { posthog } from '../../posthog';
import type { SurveyQuestion } from './survey-config';
import { surveyQuestions } from './survey-config';
import * as styles from './survey-modal.css';
import { syncUserPreferencesToPostHog } from './types';

interface SurveyModalProps {
  onClose: () => void;
  onComplete: () => void;
}

interface SurveyResponses {
  [questionId: string]: string | Record<string, string>;
}

export const SurveyModal = ({ onClose, onComplete }: SurveyModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<SurveyResponses>({});
  const [isClosing, setIsClosing] = useState(false);
  const [userName, setUserName] = useState<string | undefined>();
  const { setTheme, theme, resolvedTheme } = useTheme();
  const hasTrackedShow = useRef(false);

  const platform = environment.isTauri ? 'desktop' : 'web';

  useEffect(() => {
    if (!hasTrackedShow.current) {
      hasTrackedShow.current = true;
      posthog.capture('onboarding_survey_shown', {
        total_steps: surveyQuestions.length,
        platform,
      });
    }
  }, [platform]);

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const userInfoResult = AIProvider.userInfo;
        const userInfo =
          userInfoResult instanceof Promise
            ? await userInfoResult
            : userInfoResult;

        if (userInfo?.name) {
          setUserName(userInfo.name);

          const profileQuestion = surveyQuestions.find(q => q.id === 'profile');
          if (
            profileQuestion &&
            currentStep === surveyQuestions.indexOf(profileQuestion)
          ) {
            const names = userInfo.name.split(' ');
            const firstName = names[0] ?? '';
            const lastName = names.length > 1 ? names.slice(1).join(' ') : '';

            setResponses(prev => {
              const prevProfile =
                typeof prev['profile'] === 'object' && prev['profile'] !== null
                  ? (prev['profile'] as Record<string, string>)
                  : {};

              return {
                ...prev,
                profile: {
                  ...prevProfile,
                  firstName,
                  lastName,
                },
              };
            });
          }
        }
      } catch {
        // AIProvider might not be initialized yet, user not signed in, or other error
      }
    };

    loadUserInfo();
  }, [currentStep]);

  const currentQuestion = surveyQuestions[currentStep] as typeof surveyQuestions[number] | undefined;
  const isLastStep = currentStep === surveyQuestions.length - 1;

  const handleNext = useCallback(() => {
    const currentQuestionId = currentQuestion?.id;

    if (isLastStep) {
      const profileData =
        typeof responses['profile'] === 'object'
          ? (responses['profile'] as Record<string, string>)
          : {};
      const preferencesData =
        typeof responses['preferences'] === 'object'
          ? (responses['preferences'] as Record<string, string>)
          : {};

      syncUserPreferencesToPostHog({
        usage:
          typeof responses['usage'] === 'string' ? responses['usage'] : undefined,
        firstName: profileData['firstName'],
        lastName: profileData['lastName'],
        company: profileData['company'],
        role: profileData['role'],
        detailLevel: preferencesData['detail'],
        nudges: preferencesData['nudges'],
        noteStyle:
          typeof responses['note-style'] === 'string'
            ? responses['note-style']
            : undefined,
        displayMode:
          typeof responses['display-mode'] === 'string'
            ? responses['display-mode']
            : undefined,
      });

      posthog.capture('onboarding_survey_completed', {
        steps_completed: surveyQuestions.length,
        responses_provided: Object.keys(responses).length,
        platform,
      });

      setIsClosing(true);
      setTimeout(() => {
        onComplete();
      }, 300);
    } else {
      posthog.capture('onboarding_survey_step_completed', {
        step: currentStep + 1,
        step_id: currentQuestionId,
        total_steps: surveyQuestions.length,
        platform,
      });
      setCurrentStep(prev => prev + 1);
    }
  }, [isLastStep, responses, onComplete, currentStep, currentQuestion?.id, platform]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    posthog.capture('onboarding_survey_skipped', {
      skipped_at_step: currentStep + 1,
      skipped_at_step_id: currentQuestion?.id,
      total_steps: surveyQuestions.length,
      platform,
    });
    setIsClosing(true);
    setTimeout(() => {
      onComplete();
    }, 300);
  }, [onComplete, currentStep, currentQuestion?.id, platform]);

  const handleClose = useCallback(() => {
    posthog.capture('onboarding_survey_dismissed', {
      dismissed_at_step: currentStep + 1,
      dismissed_at_step_id: currentQuestion?.id,
      total_steps: surveyQuestions.length,
      platform,
    });
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose, currentStep, currentQuestion?.id, platform]);

  const handleSingleChoice = useCallback(
    (value: string) => {
      if (!currentQuestion) return;
      setResponses(prev => ({
        ...prev,
        [currentQuestion.id]: value,
      }));
    },
    [currentQuestion]
  );

  const handleFormChange = useCallback(
    (fieldName: string, value: string) => {
      if (!currentQuestion) return;
      setResponses(prev => {
        const existing = prev[currentQuestion.id];
        const existingObj =
          typeof existing === 'object' && existing !== null
            ? (existing as Record<string, string>)
            : {};
        return {
          ...prev,
          [currentQuestion.id]: {
            ...existingObj,
            [fieldName]: value,
          },
        };
      });
    },
    [currentQuestion]
  );

  const handlePreferenceChange = useCallback(
    (sectionId: string, value: string) => {
      if (!currentQuestion) return;
      setResponses(prev => {
        const existing = prev[currentQuestion.id];
        const existingObj =
          typeof existing === 'object' && existing !== null
            ? (existing as Record<string, string>)
            : {};
        return {
          ...prev,
          [currentQuestion.id]: {
            ...existingObj,
            [sectionId]: value,
          },
        };
      });
    },
    [currentQuestion]
  );

  const handleThemeSelect = useCallback(
    (value: string) => {
      if (!currentQuestion) return;
      setTheme(value);
      setResponses(prev => ({
        ...prev,
        [currentQuestion.id]: value,
      }));
    },
    [currentQuestion, setTheme]
  );

  const currentTheme =
    theme === 'system' ? (resolvedTheme ?? 'light') : (theme ?? 'light');

  const overlayVariants: Variants = {
    visible: {
      opacity: 1,
      transition: { duration: 0.2, ease: 'easeOut' },
    },
    hidden: {
      opacity: 0,
      transition: { duration: 0.2, ease: 'easeIn' },
    },
  };

  const containerVariants: Variants = {
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
    },
    hidden: {
      scale: 0.95,
      opacity: 0,
      transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
    },
  };

  const renderQuestion = (question: SurveyQuestion) => {
    switch (question.type) {
      case 'single-choice':
        return (
          <div className={styles.optionsGrid}>
            {question.options?.map(option => (
              <button
                key={option.value}
                className={styles.optionCard}
                data-selected={responses[question.id] === option.value}
                onClick={() => handleSingleChoice(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      case 'form':
        return (
          <div className={styles.formGrid}>
            {question.fields?.map(field => (
              <div key={field.name} className={styles.formField}>
                <label className={styles.formLabel}>{field.label}</label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder={field.placeholder}
                  value={
                    typeof responses[question.id] === 'object'
                      ? (responses[question.id] as Record<string, string>)[
                          field.name
                        ] || ''
                      : ''
                  }
                  onChange={e => handleFormChange(field.name, e.target.value)}
                />
              </div>
            ))}
          </div>
        );

      case 'preference':
        return (
          <div className={styles.preferenceSections}>
            {question.sections?.map(section => (
              <div key={section.id} className={styles.preferenceSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionIcon}>
                    {section.icon === 'detail' ? (
                      <FileText size={16} />
                    ) : (
                      <Bell size={16} />
                    )}
                  </div>
                  <h4 className={styles.sectionLabel}>{section.label}</h4>
                </div>
                <p className={styles.sectionDescription}>
                  {section.description}
                </p>
                <div className={styles.preferenceOptions}>
                  {section.options.map(option => {
                    const isSelected =
                      typeof responses[question.id] === 'object' &&
                      (responses[question.id] as Record<string, string>)[
                        section.id
                      ] === option.value;
                    return (
                      <div
                        key={option.value}
                        className={styles.preferenceOption}
                        data-selected={isSelected}
                        onClick={() =>
                          handlePreferenceChange(section.id, option.value)
                        }
                      >
                        <div
                          className={styles.radioCircle}
                          data-selected={isSelected}
                        />
                        <div className={styles.optionText}>
                          <span className={styles.optionLabel}>
                            {option.label}
                          </span>
                          {option.description && (
                            <span className={styles.optionDescription}>
                              {option.description}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );

      case 'theme-select':
        return (
          <div className={styles.themeSelectGrid}>
            {question.options?.map(option => {
              const isSelected = currentTheme === option.value;
              return (
                <label key={option.value} className={styles.themeOption}>
                  <input
                    type="radio"
                    name="theme"
                    value={option.value}
                    checked={isSelected}
                    onChange={() => handleThemeSelect(option.value)}
                    className={styles.themeRadioInput}
                  />
                  <div className={styles.themeBox} data-theme={option.value}>
                    <div className={styles.themeBoxSidebar} />
                    <div className={styles.themeBoxMain}>
                      <div className={styles.themeBoxHeader} />
                      <div className={styles.themeBoxContent}>
                        <div className={styles.themeBoxLine} />
                        <div className={styles.themeBoxLine} data-short />
                        <div className={styles.themeBoxLine} data-medium />
                      </div>
                    </div>
                  </div>
                  <span className={styles.themeOptionLabel}>
                    <svg
                      className={styles.themeCheckIcon}
                      viewBox="0 0 24 24"
                      data-visible={isSelected}
                    >
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                    <span className={styles.themeLabelText}>
                      {option.label}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className={styles.overlay}
        variants={overlayVariants}
        initial="visible"
        animate={isClosing ? 'hidden' : 'visible'}
      >
        <motion.div
          className={styles.modalContainer}
          variants={containerVariants}
          initial="visible"
          animate={isClosing ? 'hidden' : 'visible'}
        >
          <button className={styles.closeButton} onClick={handleClose}>
            <X size={20} />
          </button>

          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <h2 className={styles.greeting}>
                Hi {userName?.split(' ')[0] || 'there'}, Welcome to Pique!
              </h2>
              <p className={styles.setupText}>Let's set up your profile.</p>
            </div>

            <div className={styles.progressContainer}>
              <div className={styles.stepsRow}>
                {surveyQuestions.map((_, index) => (
                  <React.Fragment key={index}>
                    <motion.div
                      className={styles.stepCircle}
                      data-active={index === currentStep}
                      initial={false}
                      animate={{
                        scale: index === currentStep ? 1.1 : 1,
                        backgroundColor:
                          index <= currentStep
                            ? 'var(--affine-brand-color)'
                            : 'var(--affine-placeholder-color)',
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 25,
                      }}
                    >
                      {index + 1}
                    </motion.div>
                    {index < surveyQuestions.length - 1 && (
                      <motion.div
                        className={styles.stepConnector}
                        data-completed={index < currentStep}
                        initial={false}
                        animate={{
                          scaleX: index < currentStep ? 1 : 0,
                          backgroundColor:
                            index < currentStep
                              ? 'var(--affine-brand-color)'
                              : 'var(--affine-background-secondary-color)',
                        }}
                        transition={{
                          type: 'spring',
                          stiffness: 200,
                          damping: 30,
                          delay: 0.1,
                        }}
                        style={{ transformOrigin: 'left' }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {currentQuestion && (
            <div className={styles.questionContainer}>
              <div>
                <div className={styles.questionHeader}>
                  <div className={styles.questionNumber}>{currentStep + 1}</div>
                  <h3 className={styles.questionTitle}>
                    {currentQuestion.title}
                  </h3>
                </div>
                {currentQuestion.subtitle && (
                  <p className={styles.questionSubtitle}>
                    {currentQuestion.subtitle}
                  </p>
                )}
              </div>

              {renderQuestion(currentQuestion)}
            </div>
          )}

          <div className={styles.footer}>
            <button className={styles.skipButton} onClick={handleSkip}>
              Skip for now
            </button>
            <div className={styles.navigationButtons}>
              {currentStep > 0 && (
                <button className={styles.backButton} onClick={handleBack}>
                  <ArrowLeft size={16} />
                  Back
                </button>
              )}
              <button className={styles.nextButton} onClick={handleNext}>
                {isLastStep ? 'Finish' : 'Next'}
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
